import { toast } from "sonner";
import { Loader } from "@/shared/ui/Loader";
import { Button } from "@/components/ui/button";


import { useNavigate, useParams } from "@tanstack/react-router";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { ArrowLeftIcon, SendIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  usePromotionPushMonitorQuery,
  usePromotionQuery,
  useResendPromotionPushMutation,
  useUpdatePromotionMutation,
} from "../api/queries";
import { PromotionForm } from "../ui/PromotionForm";
import { MarketingPushMonitorPanel } from "@/shared/notifications/MarketingPushMonitorPanel";
import { confirm } from "@/shared/lib/confirm";

export function PromotionEditPage() {
  const { promotionId } = useParams({
    from: "/app/loyalty/promotions/$promotionId/edit",
  });
  const nav = useNavigate({
    from: "/app/loyalty/promotions/$promotionId/edit",
  });
  const {
    data: promotion,
    isLoading,
    error,
  } = usePromotionQuery(promotionId ?? "");
  const canManage = useActionPermission(["loyalty:update"]);
  const updatePromotion = useUpdatePromotionMutation();
  const resendPush = useResendPromotionPushMutation();
  const pushMonitor = usePromotionPushMonitorQuery(promotionId ?? "");

  if (!promotionId) return null;

  if (isLoading && !promotion) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ໂປຣໂມຊັນ",
                to: "/app/loyalty/promotions",
              },
              {
                label: "ແກ້ໄຂໂປຣໂມຊັນ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        </Main>
      </>
    );
  }

  if (error || !promotion) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ໂປຣໂມຊັນ",
                to: "/app/loyalty/promotions",
              },
              {
                label: "ແກ້ໄຂໂປຣໂມຊັນ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-destructive">
            ບໍ່ພົບໂປຣໂມຊັນ
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/loyalty/promotions" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </Main>
      </>
    );
  }

  const handleResendPush = async () => {
    const ok = await confirm({
      title: "ສົ່ງ push ອີກຄັ້ງ",
      description: `ສົ່ງ push ໄປແອັບລູກຄ້າສຳລັບ «${promotion.name}»?`,
      actionText: "ສົ່ງ push",
      cancelText: "ຍົກເລີກ",
    });
    if (!ok) return;
    try {
      await resendPush.mutateAsync(promotionId);
      toast.success("ເພີ່ມ push ເຂົ້າຄິວແລ້ວ — worker ຈະສົ່ງໃນໄວໆ");
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "ບໍ່ສາມາດສົ່ງ push ອີກຄັ້ງໄດ້";
      toast.error(msg);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ໂປຣໂມຊັນ",
              to: "/app/loyalty/promotions",
            },
            {
              label: "ແກ້ໄຂໂປຣໂມຊັນ",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂໂປຣໂມຊັນ</h2>
            <p className="text-muted-foreground">
              ປັບກົດໂປຣໂມຊັນ ເວລາ ແລະ ຂອບເຂດການສົມຄວນ
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/loyalty/promotions" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            {canManage && (
              <Button
                variant="secondary"
                onClick={handleResendPush}
                disabled={resendPush.isPending || !promotion.isActive}
                title={
                  promotion.pushSentAt
                    ? `ສົ່ງຄັ້ງລ່າສຸດ: ${promotion.pushSentAt}`
                    : "ຍັງບໍ່ທັນສົ່ງ push"
                }
              >
                <SendIcon className="size-4" />
                ສົ່ງ push ອີກຄັ້ງ
              </Button>
            )}
          </div>
        </div>

        <PromotionForm
          syncKey={promotion.updatedAt}
          defaultValues={{
            name: promotion.name,
            imageUrl: promotion.imageUrl ?? null,
            type: promotion.type,
            startAt: promotion.startAt,
            endAt: promotion.endAt,
            minOrderTotal: promotion.minOrderTotal ?? null,
            percentValue: promotion.percentValue ?? null,
            fixedAmount: promotion.fixedAmount ?? null,
            globalLimit: promotion.globalLimit ?? null,
            perCustomerLimit: promotion.perCustomerLimit ?? null,
            priority: promotion.priority,
            showOnOpen: promotion.showOnOpen,
            repeatOnOpen: promotion.repeatOnOpen,
            sendPush: promotion.sendPush,
            isActive: promotion.isActive,
            scopes: (() => {
              const byType = new Map<string, string[]>();
              for (const s of promotion.scopes) {
                const list = byType.get(s.scopeType) ?? [];
                list.push(s.scopeId);
                byType.set(s.scopeType, list);
              }
              return Array.from(byType.entries()).map(([scopeType, scopeIds]) => ({
                scopeType: scopeType as "BRANCH" | "PRODUCT" | "CATEGORY",
                scopeIds,
              }));
            })(),
          }}
          onSubmit={async (payload) => {
            try {
              await updatePromotion.mutateAsync({ id: promotionId, payload });
              toast.success("ບັນທຶກໂປຣໂມຊັນແລ້ວ");
            } catch {
              //
            }
          }}
          submitting={updatePromotion.isPending}
          onCancel={() => nav({ to: "/app/loyalty/promotions" })}
          submitLabel="ບັນທຶກການປ່ຽນແປງ"
        />

        <div className="mt-8">
          <MarketingPushMonitorPanel
            data={pushMonitor.data}
            isLoading={pushMonitor.isLoading}
            isFetching={pushMonitor.isFetching}
            onRefresh={() => void pushMonitor.refetch()}
          />
        </div>
      </Main>
    </>
  );
}
